import React, { useState, useEffect } from "react";
import activity_list from "mockUp/homepage.json";
import { Row, Col, Carousel, Button, Tag } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import Fig84 from "assets/homepage/Frame84.png";
import Fig85 from "assets/homepage/Frame85.png";
import Fig86 from "assets/homepage/Frame86.png";
import Fig87 from "assets/homepage/Frame87.png";
import Logo3 from "assets/homepage/LOGO3.png";
import Logo4 from "assets/homepage/LOGO4.png";
import DefaultHomeBg from "assets/homepage/defaultHomeBg.png";

import { convertTimeStamp } from "utility/functions/convertTimestamp";

import { apiGetActivityList } from "api/api";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "utility/hooks/useSiteConfig";
import { CONFIG_GROUP } from "config/siteContent";

// # 體驗 icon：中文版桌機會在下方多一行英文副標
const EXPERIENCE_ICONS = [
  { key: "iconHeadphone", image: Fig84 },
  { key: "iconChair", image: Fig85 },
  { key: "iconCityViews", image: Fig86 },
  { key: "iconFood", image: Fig87 },
];

const Homepage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === "en";
  const config = useSiteConfig(CONFIG_GROUP.HOMEPAGE);
  const [title, setTitle] = useState("");
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [rowData, setRowData] = useState([]);

  // # 獲取列表
  const fetchData = async ({ title, limit, offset }) => {
    try {
      const res = await apiGetActivityList({ title, limit, offset });
      const { content } = res.data;
      setRowData(content);
    } catch (err) {
    } finally {
    }
  };

  useEffect(() => {
    fetchData({ title, limit, offset });
  }, []);

  const handleToActivity = (item) => {
    navigate(`/activity/${item.id}`);
  };

  return (
    <div>
      {/* 主視覺：固定圖片（不串活動），文案與圖片由後台維護 */}
      <section className="relative h-[400px] w-full overflow-hidden">
        <img
          className="absolute inset-0 h-full w-full object-cover object-center"
          alt={config.get("heroLabel") || "Skyline Film"}
          src={config.get("heroImageUrl") || DefaultHomeBg}
          onError={(e) => {
            e.currentTarget.src = DefaultHomeBg;
          }}
        />

        {/* 底部漸層，讓文字可讀 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.75) 75%, rgba(0,0,0,0.95) 100%)",
          }}
        />

        {/* 文字靠左對齊下方內容區（max-w-1440 + desktop:px-40） */}
        <div className="pointer-events-none absolute bottom-0 left-1/2 w-full max-w-[1440px] -translate-x-1/2 px-6 pb-10 text-left tablet:px-10 desktop:px-40 desktop:pb-14">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[3px] text-SKF-Green-001">
            {config.get("heroLabel")}
          </p>
          <h1 className="mb-2 max-w-[600px] text-3xl font-bold leading-tight tracking-tight text-white desktop:text-5xl">
            {config.getLines("heroTitle").map((line, i) => (
              <React.Fragment key={line}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </h1>
          <p className="m-0 text-sm leading-relaxed text-SKF-G-002 desktop:text-[15px]">
            {config.getLines("heroSubtitle").map((line, i) => (
              <React.Fragment key={line}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </p>
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto">
        <div className="px-0 desktop:px-40 tablet:px-10 ">
          {/* 即將上映 */}
          <div className="mobile:px-2 pt-10 desktop:pt-20 desktop:mb-32">
            <div className="mb-8 flex items-baseline justify-between">
              <h2 className="m-0 text-[22px] font-bold tracking-wide text-white">
                {t("homepage.upcomingTitle")}
              </h2>
            </div>

            {/* 點數方案推廣 */}
            {config.isOn("isPointsPromoEnabled") && (
              <div
                role="button"
                tabIndex={0}
                onClick={() => navigate("/points")}
                onKeyDown={(e) => e.key === "Enter" && navigate("/points")}
                className="mb-6 flex cursor-pointer flex-col gap-5 rounded-xl border border-solid border-SKF-Green-001/30 bg-gradient-to-br from-[#112E24] to-[#0C1F18] px-7 py-6 text-left transition duration-200 hover:border-SKF-Green-001/60 laptop:flex-row laptop:items-center laptop:justify-between laptop:px-10 laptop:py-7"
              >
                <div className="flex flex-col gap-4 laptop:flex-row laptop:items-center laptop:gap-8">
                  <div>
                    <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[2px] text-SKF-Green-001">
                      {config.get("pointsPromoLabel")}
                    </p>
                    <p className="mb-1 text-xl font-bold text-white">
                      {config.get("pointsPromoTitle")}
                    </p>
                    <p className="m-0 text-[13px] text-white/50">
                      {config.get("pointsPromoDescription")}
                    </p>
                  </div>
                  <ul className="m-0 flex list-none flex-col gap-2.5 p-0 laptop:flex-row laptop:gap-5">
                    {config.getLines("pointsPromoFeatures").map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-1.5 text-[13px] text-white/65"
                      >
                        <CheckIcon className="w-3.5 shrink-0 text-SKF-Green-001" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <span className="inline-flex shrink-0 items-center justify-center gap-1.5 self-stretch rounded-md bg-SKF-Green-001 px-5 py-2.5 text-[13px] font-bold text-black laptop:self-auto">
                  {config.get("pointsPromoButtonText")}
                  <ArrowRightOutlined />
                </span>
              </div>
            )}

            {/* 活動列表 */}
            <div className="grid grid-cols-1 gap-5 laptop:grid-cols-2">
              {rowData.map((item, i) => (
                <div
                  key={i}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleToActivity(item)}
                  onKeyDown={(e) => e.key === "Enter" && handleToActivity(item)}
                  className="flex cursor-pointer flex-col overflow-hidden rounded-xl border border-solid border-white/[0.07] bg-[#111] text-left transition duration-200 hover:-translate-y-0.5 hover:border-white/20"
                >
                  <div className="relative">
                    <picture>
                      <source
                        media="(max-width: 520px)"
                        srcSet={encodeURI(item.mobileCover)}
                      />
                      <img
                        src={item.webCover}
                        className="block aspect-video w-full object-cover"
                        alt={item.title}
                      />
                    </picture>
                    {Number(item.status) === 1 && (
                      <Tag className="absolute left-3 top-3 z-10 m-0 rounded-lg border-0 bg-[#FAAD14] px-2 py-0 text-black">
                        {t("homepage.draft")}
                      </Tag>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col gap-2 px-5 pb-6 pt-5">
                    <p className="m-0 text-[17px] font-bold leading-snug text-white">
                      {item.title}
                    </p>
                    <p className="m-0 mt-1 text-[13px] text-SKF-G-002">
                      {convertTimeStamp(item.startDateTime)}
                    </p>
                  </div>
                  <div className="px-5 pb-5">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-white px-4 py-2 text-[13px] font-bold text-black transition hover:opacity-85">
                      {t("homepage.buyTicket")}
                      <ArrowRightOutlined />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 體驗wording */}
          <Row className="md-20 desktop:mb-32">
            <Col sm={{ span: 12, offset: 6 }}>
              <h3>{t("homepage.expTitle")}</h3>
              <p className="px-3 desktop:px-0 text-SKF-G-002">
                {t("homepage.expContent")}
              </p>
            </Col>
          </Row>

          {/* 體驗icon */}
          <Row className="mt-12 md-20 desktop:mb-32 mx-0">
            {EXPERIENCE_ICONS.map(({ key, image }) => (
              <Col key={key} sm={6} xs={12} className="mb-4">
                <img src={image} className="w-4/5 m-auto mb-2 desktop:mb-8" />
                <h4 className="m-0">{t(`homepage.${key}`)}</h4>
                {/* 英文版主標已經是英文，不再重複副標 */}
                {!isEnglish && (
                  <h4 className="m-0 hidden desktop:block">
                    {t(`homepage.${key}`, { lng: "en" })}
                  </h4>
                )}
              </Col>
            ))}
          </Row>

          {/* 贊助廠商 */}
          {/* <Row className="text-center md-20 desktop:mb-32">
          <Col span={24} className="mb-4">
            <h3>
              <span>合作夥伴</span>
              <span className="hidden desktop:inline-block desktop:ml-2">Sponsors</span>
            </h3>
          </Col>
          <Col sm={{ span: 3, offset: 9 }} xs={{ span: 11, offset: 1 }}>
            <img src={Logo3} />
          </Col>
          <Col sm={{ span: 3 }} xs={{ span: 11 }}>
            <img src={Logo4} />
          </Col>
          <Col
            className="my-4"
            sm={{ span: 8, offset: 8 }}
            xs={{ span: 16, offset: 4 }}
          >
            <Button
              type="link"
              className="text-[#fff]"
              icon={<ArrowRightOutlined />}
              onClick={() => navigate("/corporation")}
            >
              想成為我們的年度合作夥伴？
            </Button>
          </Col>
        </Row> */}

          {/* 與我們合作 */}
          <Row className="desktop:mb-32 mobile:px-2 tablet:mt-10 mobile:mt-10">
            <Col
              sm={12}
              xs={24}
              className="min-h-96 desktop:min-h-[500px] bg-[url('assets/homepage/Corporation.png')] desktop:bg-[url('assets/homepage/Corporation.png')] bg-no-repeat bg-cover mobile:rounded-t-3xl desktop:rounded-l-3xl tablet:rounded-l-3xl"
            ></Col>
            <Col
              sm={12}
              xs={24}
              className="min-h-96 desktop:min-h-[500px] flex-col mobile:rounded-b-3xl desktop:rounded-r-3xl tablet:rounded-r-3xl bg-SKF-Green-001 text-black text-left px-8 py-11 mobile:py-4 desktop:pt-28"
            >
              <h3 className="mb-0 font-body">{t("homepage.becomePartner")}</h3>
              <p className="text-SKF-G-003">
                {t("homepage.becomePartnerContent")}
                <br />
                <br />
                {t("homepage.becomePartnerSubContent")}
              </p>
              <div>
                <Button
                  className="mt-8 bg-black text-white"
                  onClick={() => navigate(`/corporation`)}
                >
                  {t("homepage.readMore")}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Homepage;
